/** @format */
import { useMeasure } from '@mpb-com/feather/hooks';
import { useSpring } from '@react-spring/web';
import { Vector2, useGesture } from '@use-gesture/react';
import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';

import { isRotationStyle } from '@toucan/client/shared/hooks/use-carousel/type-guards';

import {
    flipMovementDirection,
    getNextIndex,
    getRotationElementStyle,
} from './helper';

export enum CarouselStyle {
    SLIDE = 'SLIDE',
    ROTATE = 'ROTATE',
}

type UseCarouselCommonProps = {
    itemLength: number;
};

export type UseCarouselSlideProps = UseCarouselCommonProps & {
    carouselStyle: CarouselStyle.SLIDE;
};

export type UseCarouselRotateProps = UseCarouselCommonProps & {
    carouselStyle: CarouselStyle.ROTATE;
    arcOfRotation: number;
    /**
     * Percentage of carousel height
     */
    radiusOfRotationArc: number;
};

export type UseCarouselProps = UseCarouselSlideProps | UseCarouselRotateProps;

export type UseCarouselReturn<ElementType> = {
    carouselRef: RefObject<ElementType>;
    activeIndex: number;
    isPrevDisabled: boolean;
    isNextDisabled: boolean;
    handlePrev: () => void;
    handleNext: () => void;
    handleNavigation: (index: number) => void;
    style: any;
    getElementStyle: (index: number) => CSSProperties | undefined;
};

export const useCarousel = <ElementType extends HTMLElement>(
    props: UseCarouselProps
): UseCarouselReturn<ElementType> => {
    const { itemLength } = props;
    const carouselRef = useRef<ElementType | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const { width } = useMeasure(carouselRef);

    const [transformStep, setTransformStep] = useState(
        isRotationStyle(props) ? props.arcOfRotation / itemLength : 0
    );

    const transformAmount = useRef(0);

    const upperBound = itemLength - 1;

    const isPrevDisabled = activeIndex === 0;
    const isNextDisabled = activeIndex === upperBound;

    const [style, controls] = useSpring(
        {
            transformOrigin: isRotationStyle(props)
                ? `center ${props.radiusOfRotationArc + 50}%`
                : undefined,
            transform: isRotationStyle(props)
                ? `rotate(${transformAmount.current * -1}deg)`
                : `translateX(${transformAmount.current * -1}px)`,
        },
        []
    );

    useGesture(
        {
            onDrag: handleDrag,
            onDragEnd: handleDragEnd,
        },
        {
            target: carouselRef,
            axis: 'x',
            drag: {
                threshold: 10,
            },
        }
    );

    function updateTransform(amount: number) {
        controls.start(() => {
            return {
                transform: isRotationStyle(props)
                    ? `rotate(${amount * -1}deg)`
                    : `translateX(${amount * -1}px)`,
            };
        });
    }

    function handleDrag({ movement: [xMovement] }: { movement: Vector2 }) {
        const adjustAmount = isRotationStyle(props)
            ? xMovement * 0.1
            : xMovement;
        const amount =
            transformAmount.current + flipMovementDirection(adjustAmount);

        updateTransform(amount);
    }

    function handleDragEnd({ movement: [xMovement] }: { movement: Vector2 }) {
        const nextIndex = getNextIndex(transformStep, xMovement, activeIndex);

        const isOutOfBound = nextIndex < 0 || nextIndex > upperBound;

        if (isOutOfBound) {
            resetToActiveIndex();
            return;
        }

        handleNavigation(nextIndex);
    }

    function resetToActiveIndex() {
        const amount = transformStep * activeIndex;
        transformAmount.current = amount;

        updateTransform(amount);
    }

    function handleNavigation(index: number) {
        const amount = transformStep * index;
        transformAmount.current = amount;

        setActiveIndex(index);
        updateTransform(amount);
    }

    function handlePrev() {
        if (isPrevDisabled) return;

        handleNavigation(activeIndex - 1);
    }

    function handleNext() {
        if (isNextDisabled) return;

        handleNavigation(activeIndex + 1);
    }

    function getElementStyle(index: number) {
        if (isRotationStyle(props)) {
            return getRotationElementStyle(
                index,
                props.arcOfRotation,
                props.radiusOfRotationArc,
                itemLength
            );
        }

        return undefined;
    }

    useEffect(() => {
        if (!carouselRef?.current) return;

        const childElement = carouselRef?.current?.children?.[0];

        if (!childElement || isRotationStyle(props)) return;

        setTransformStep(
            parseInt(
                window
                    .getComputedStyle(childElement)
                    .getPropertyValue('margin-right')
            ) + childElement.clientWidth
        );
    }, [setTransformStep, width]);

    useEffect(() => {
        resetToActiveIndex();
    }, [transformStep]);

    return {
        carouselRef,
        activeIndex,
        isPrevDisabled,
        isNextDisabled,
        handlePrev,
        handleNext,
        handleNavigation,
        style,
        getElementStyle,
    };
};
